import { z } from 'zod';
import { PositiveDecimalString, UuidString } from '../../primitives.js';
import { FieldType } from './field-definition.js';
import { ProjectType } from './project.js';
import { StateGroup } from './state.js';
import { DependencyType } from './task-dependency.js';
import { EstimateUom, TaskKind, TaskPriority } from './task.js';

/**
 * TemplateSnapshot v2 — снапшот проекта как шаблон (project-templates §2).
 *
 * Шаблон = versioned-снапшот проекта в `task_module_blueprints.snapshot jsonb`:
 * иерархия, workflow-статусы, кастом-поля, метки, вехи, смещения дат от якоря.
 *
 * Инварианты (SOLUTION §1):
 *   - I1 снапшот инертен (не строка в живых таблицах);
 *   - I3 ни одной абсолютной даты — только `*_offset_days` от якоря;
 *   - I4 `field_definitions.key` копируется как есть → `custom_fields` валиден в копии;
 *   - I5 всё отброшенное — в `skipped` (enum-коды + counts).
 *
 * Zod-строгость: stored-entity → БЕЗ `.strict()` (forward-compat, инвариант B6).
 * `.strict()` — только на input-схемах ручек. Внутренние ссылки — по локальным
 * `key` (снапшот самодостаточен, не зависит от uuid источника).
 */

// ─── Skip-коды (I5) — единый источник для BE-отчёта и FE-локализации ─────

/**
 * Коды причин пропуска (сериализация + материализация). FE (F2.2) держит
 * единую карту `код → i18n`; BE шлёт только counts. Zero-категории FE скрывает.
 */
export const TEMPLATE_SKIP_CODES = [
  'external_deps',
  'secondary_tasks',
  'done_tasks',
  'recurring_instances',
  'attachments',
  'comments',
  'worklogs',
  'archived_tasks',
  'date_custom_fields',
  'orphan_custom_field_keys',
  'missing_entity_labels',
] as const;
export type TemplateSkipCode = (typeof TEMPLATE_SKIP_CODES)[number];

const count = () => z.number().int().nonnegative().default(0);

export const TemplateSkipped = z.object({
  /** Кросс-проектные / внешние связи (оба конца не в снапшоте). */
  external_deps: count(),
  /** Задачи с secondary-принадлежностью источнику (не primary-junction). */
  secondary_tasks: count(),
  /** Задачи в completed/cancelled при exclude_done. */
  done_tasks: count(),
  /** Сгенерированные recurring-инстансы (момент, не план). */
  recurring_instances: count(),
  attachments: count(),
  comments: count(),
  worklogs: count(),
  /** archived_at IS NOT NULL / is_draft. */
  archived_tasks: count(),
  /** date-typed значения custom_fields, обнулённые при сериализации (I3). */
  date_custom_fields: count(),
  /** Ключи custom_fields от удалённых field-defs. */
  orphan_custom_field_keys: count(),
  /** entity-wide метки, не найденные при материализации (I7). */
  missing_entity_labels: count(),
});
export type TemplateSkipped = z.infer<typeof TemplateSkipped>;

// ─── Sub-schemas ─────────────────────────────────────────────────────

export const SnapshotSource = z.object({
  project_id: UuidString,
  project_name: z.string(),
  project_code: z.string().nullable().default(null),
});

export const SnapshotProject = z.object({
  project_type: ProjectType.nullable().default(null),
  /** Decimal-string (план); значения формы материализации выигрывают (§7). */
  estimated_cost_amount: z.string().nullable().default(null),
  currency: z.string().default('KZT'),
  notes: z.string().nullable().default(null),
});

/** color_token — token-string (не enum): stored-entity forward-compat. */
export const SnapshotState = z.object({
  key: z.string().min(1).max(64),
  name: z.string().min(1).max(100),
  color_token: z.string().min(1).max(64),
  group: StateGroup,
  sequence: z.number().finite(),
  is_default: z.boolean(),
});

export const SnapshotFieldDefinition = z.object({
  key: z.string().min(1).max(40),
  label: z.string().min(1).max(100),
  type: FieldType,
  required: z.boolean().default(false),
  default_value: z.unknown().nullable().default(null),
  constraints: z.record(z.string(), z.unknown()).default({}),
  sequence: z.number().finite(),
  template_id: UuidString.nullable().default(null),
});

export const SnapshotLabel = z.object({
  key: z.string().min(1).max(64),
  name: z.string().min(1).max(120),
  color_token: z.string().min(1).max(64),
});

export const SnapshotMilestone = z.object({
  key: z.string().min(1).max(64),
  name: z.string().min(1),
  position: z.number().int().nonnegative().default(0),
  deadline_offset_days: z.number().int().nullable().default(null),
  /** numeric(6,3) как decimal-string. */
  billing_pct: z.string().nullable().default(null),
  linked_task_key: z.string().max(64).nullable().default(null),
});

/** Ассайни по key/id (без row-id: снапшот самодостаточен). */
export const SnapshotAssignee = z.discriminatedUnion('type', [
  z.object({ type: z.literal('user'), user_id: UuidString }),
  z.object({ type: z.literal('role'), role_key: z.string().min(1).max(64) }),
  z.object({ type: z.literal('group'), group_id: UuidString }),
]);

const snapshotTaskShape = {
  key: z.string().min(1).max(64),
  title: z.string().min(1).max(500),
  /** TipTap JSON pass-through (как в Task). */
  description_json: z.unknown(),
  description_text: z.string().default(''),
  kind: TaskKind,
  priority: TaskPriority.default('none'),
  estimate_value: PositiveDecimalString.nullable().default(null),
  estimate_uom: EstimateUom.nullable().default(null),
  /** Иерархия по локальному key. */
  parent_key: z.string().max(64).nullable().default(null),
  sort_order: z.number().finite(),
  /** null = бездатная сторона (I3); folder ⇒ обе null. */
  start_offset_days: z.number().int().nullable().default(null),
  due_offset_days: z.number().int().nullable().default(null),
  assignees: z.array(SnapshotAssignee).default([]),
  label_keys: z.array(z.string()).default([]),
  entity_label_ids: z.array(UuidString).default([]),
  custom_fields: z.record(z.string(), z.unknown()).default({}),
};

/** Общие refine: paired-null estimate + folder-инвариант (offsets/estimate null). */
function refineTask<T extends z.ZodRawShape>(schema: z.ZodObject<T>) {
  return schema
    .refine(
      (d) =>
        ((d as { estimate_value: unknown; estimate_uom: unknown }).estimate_value === null) ===
        ((d as { estimate_uom: unknown }).estimate_uom === null),
      {
        message: 'estimate_value и estimate_uom — оба null или оба заданы',
        path: ['estimate_uom'],
      },
    )
    .refine(
      (d) => {
        const t = d as {
          kind: string;
          start_offset_days: number | null;
          due_offset_days: number | null;
          estimate_value: unknown;
        };
        return (
          t.kind !== 'folder' ||
          (t.start_offset_days === null && t.due_offset_days === null && t.estimate_value === null)
        );
      },
      { message: 'Папка не имеет собственных дат и оценки', path: ['kind'] },
    );
}

/**
 * Un-refined объект задачи. Нужен input-схеме ручек (B5): `.strict()` на
 * ZodEffects не вызвать, а сами refine'ы там заменены структурными гвардами с
 * машинными кодами (freeform-сообщения refine'ов в UI не годятся, i18n №19).
 */
export const SnapshotTaskObject = z.object(snapshotTaskShape);

export const SnapshotTask = refineTask(SnapshotTaskObject);
export type SnapshotTask = z.infer<typeof SnapshotTask>;

export const SnapshotDependency = z.object({
  from_key: z.string().min(1).max(64),
  to_key: z.string().min(1).max(64),
  relation_type: DependencyType,
});

// ─── TemplateSnapshot (persisted) ────────────────────────────────────

const snapshotShape = {
  schema_version: z.literal(2),
  /**
   * B4 (template-editor-and-dates §4.3) — nullable: шаблон, созданный с нуля
   * (`POST /blueprints`), проекта-источника не имеет. Все читатели обязаны
   * идти через `source?.` с падением на имя шаблона.
   */
  source: SnapshotSource.nullable(),
  project: SnapshotProject,
  states: z.array(SnapshotState).default([]),
  field_definitions: z.array(SnapshotFieldDefinition).default([]),
  labels: z.array(SnapshotLabel).default([]),
  entity_label_ids: z.array(UuidString).default([]),
  milestones: z.array(SnapshotMilestone).default([]),
  tasks: z.array(SnapshotTask).default([]),
  dependencies: z.array(SnapshotDependency).default([]),
  skipped: TemplateSkipped.default({}),
};

/** refine: если статусы есть — ровно один is_default (§2.1). */
function refineOneDefault<T extends z.ZodRawShape>(schema: z.ZodObject<T>) {
  return schema.refine(
    (d) => {
      const states = (d as { states: Array<{ is_default: boolean }> }).states;
      if (states.length === 0) return true;
      return states.filter((s) => s.is_default).length === 1;
    },
    { message: 'В снапшоте должен быть ровно один статус по умолчанию', path: ['states'] },
  );
}

export const TemplateSnapshot = refineOneDefault(z.object(snapshotShape));
export type TemplateSnapshot = z.infer<typeof TemplateSnapshot>;

/**
 * B7 — стартовый workflow шаблона, созданного с нуля. Совпадает с DEFAULT_STATES
 * материализатора (`task-module-materializer-service.ts`), вынесен сюда, чтобы
 * два набора не разъехались: проект из scratch-шаблона обязан выглядеть так же,
 * как проект из v1-шаблона.
 */
export const DEFAULT_SNAPSHOT_STATES: Array<z.infer<typeof SnapshotState>> = [
  {
    key: 'todo',
    name: 'К выполнению',
    color_token: 'slate',
    group: 'unstarted',
    sequence: 10,
    is_default: true,
  },
  {
    key: 'in_progress',
    name: 'В работе',
    color_token: 'blue',
    group: 'started',
    sequence: 20,
    is_default: false,
  },
  {
    key: 'done',
    name: 'Готово',
    color_token: 'green',
    group: 'completed',
    sequence: 30,
    is_default: false,
  },
];

/** Пустой валидный снапшот шаблона «с нуля» (§4.3): только workflow, без содержимого. */
export function emptyTemplateSnapshot(): TemplateSnapshot {
  return TemplateSnapshot.parse({
    schema_version: 2,
    source: null,
    project: {},
    states: DEFAULT_SNAPSHOT_STATES,
    field_definitions: [],
    labels: [],
    entity_label_ids: [],
    milestones: [],
    tasks: [],
    dependencies: [],
    skipped: {},
  });
}

// ─── TemplateSnapshotExtended (in-memory, для duplicate §5) ───────────

/** Extended-задача несёт статус и timestamps (persisted-снапшот их не хранит). */
export const SnapshotTaskExtended = refineTask(
  z.object({
    ...snapshotTaskShape,
    /** ключ статуса из states[] (для statuses:'keep'). */
    state_key: z.string().min(1).max(64).nullable().default(null),
    started_at: z.string().nullable().default(null),
    completed_at: z.string().nullable().default(null),
  }),
);
export type SnapshotTaskExtended = z.infer<typeof SnapshotTaskExtended>;

export const SnapshotMember = z.object({
  user_id: UuidString,
  role_in_project: z.string().default('member'),
});

export const TemplateSnapshotExtended = refineOneDefault(
  z.object({
    ...snapshotShape,
    tasks: z.array(SnapshotTaskExtended).default([]),
    members: z.array(SnapshotMember).default([]),
    /** Extended-вехи несут прогресс (для statuses:'keep'). */
    milestones: z
      .array(
        SnapshotMilestone.extend({
          is_reached: z.boolean().default(false),
          reached_date: z.string().nullable().default(null),
        }),
      )
      .default([]),
  }),
);
export type TemplateSnapshotExtended = z.infer<typeof TemplateSnapshotExtended>;
